'use client';

import { useSyncExternalStore } from 'react';
import posthog, { type PostHog, type PostHogConfig } from 'posthog-js';

// Browser PostHog client. Initialized once from the PostHogProvider. Disabled
// (returns null everywhere) unless NEXT_PUBLIC_POSTHOG_KEY is set and
// POSTHOG_ENABLED is not "false", so dev and unconfigured envs send nothing.

let initialized = false;

const PII_MASK_SELECTOR = [
  '[class*="email"]',
  '[class*="phone"]',
  '[class*="card"]',
  '[class*="account"]',
  'input[type="email"]',
  'input[type="tel"]',
  'input[type="password"]',
].join(', ');

export function isPostHogEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return false;
  if (process.env.POSTHOG_ENABLED === 'false') return false;
  return true;
}

export function initPostHog(): PostHog | null {
  if (!isPostHogEnabled()) return null;
  if (initialized) return posthog;

  const isDev = process.env.NODE_ENV === 'development';

  const config: Partial<PostHogConfig> = {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST ?? 'https://us.posthog.com',
    persistence: isDev ? 'memory' : 'localStorage+cookie',
    disable_session_recording: false,
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: PII_MASK_SELECTOR,
      recordCanvas: false,
    } as PostHogConfig['session_recording'],
    loaded: (instance) => {
      if (isDev) {
        instance.debug(false);
      }
    },
  };

  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, config);
  initialized = true;
  return posthog;
}

export function getPostHog(): PostHog | null {
  if (!isPostHogEnabled()) return null;
  if (!initialized) return initPostHog();
  return posthog;
}

export function resetPostHog(): void {
  if (!initialized) return;
  posthog.reset();
}

const subscribe = () => () => {};

export function usePostHog(): PostHog | null {
  return useSyncExternalStore<PostHog | null>(subscribe, getPostHog, () => null);
}

export { posthog };
